import { faEllipsisVertical, faHourglass, faPen, faBan } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useInvoicesPermissions } from '../../hooks/useInvoicesPermissions';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { Button } from '@shared/components/ui/button';

interface ShipmentDropdownActionsProps {
  shipmentId: number;
  onAction?: (action: string, shipmentId: number) => void;
}

/**
 * Componente de acciones para shipments en la vista de Invoices
 * Muestra 3 opciones: Prefacturar, Editar y Bloquear
 * Cada acción se muestra solo si el usuario tiene el permiso correspondiente
 */
export const ShipmentDropdownActions = ({ shipmentId, onAction }: ShipmentDropdownActionsProps) => {
  const { t } = useTranslation('invoicesOrdersModule');

  // 🔒 Verificar permisos con hook específico de Invoices (incluye debug mode)
  const { canCreatePreinvoices, canEditLines, canBlockLines } = useInvoicesPermissions();

  const handleAction = (actionType: string) => {
    if (onAction) {
      onAction(actionType, shipmentId);
    }
  };

  // 🎯 Definir todas las acciones posibles con sus permisos
  const allActions = [
    {
      id: 'prefacturar',
      label: t('buttons.prefacturar', 'Prefacturar'),
      icon: faHourglass,
      action: 'prefacturar',
      canShow: canCreatePreinvoices,
    },
    {
      id: 'edit',
      label: 'Editar',
      icon: faPen,
      action: 'edit',
      canShow: canEditLines,
    },
    {
      id: 'block',
      label: 'Bloquear',
      icon: faBan,
      action: 'block',
      canShow: canBlockLines,
    },
  ];

  // 🔒 Filtrar acciones según permisos
  const actions = allActions.filter((action) => action.canShow);

  if (actions.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid="shipment-dropdown-trigger">
          <FontAwesomeIcon icon={faEllipsisVertical} className="text-icons" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-48 border-card-border" data-testid="shipment-dropdown-content">
        {actions.map((action) => (
          <DropdownMenuItem
            key={action.id}
            onClick={() => handleAction(action.action)}
            className="whitespace-nowrap"
            data-testid={`shipment-action-${action.id}`}
          >
            <FontAwesomeIcon icon={action.icon} className="mr-1 !w-3 !h-3 !text-content-primary" />
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
